import PageWrapper from "app/components/page-wrapper/page-wrapper";
import { cardInfo, onboardingTexts } from "app/config";
import useUTMTracking from "app/hooks/useUtmLinksTracker";
import { useTheme } from "app/shared";
import HeaderConnect from "app/widgets/crypto-header/header-connect";
import Leaderboard from "app/widgets/leaderboard/leaderboard";
import QuestClaimrWidget from "app/widgets/quest-claimr-widget/quest-claimr-widget";
import LockedLiderBoard from "assets/img/lockedLiderboard.png";
import { FC, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useAccount } from "wagmi";
import withQueryParams from "../components/withQueryParams";

import CircleLoader from "app/components/loader/CircleLoader";
import Banner from "app/widgets/banner/banner";
import {
  useGetPromotionBannersQuery,
  useGetUsersByCompleteQuestXpQuery,
  useGetUsersByRefXpQuery,
  useLazyGetClaimrTokenQuery,
} from "store/api/new.api";
import FullScreenLoader from "../components/loader/FullScreenLoader";
import styles from "./onboarding.page.module.scss";

interface LocationState {
  scrollToKarmaSection?: boolean;
}


const CLAIMR_WIDGET_LOADING_TIMOUT = 20 * 1000;

const OnboardingPage: FC<{ questId?: string }> = ({ questId }) => {
  useUTMTracking();
  const { address } = useAccount();
  const isAuth = !!address;
  const [claimrLoading, setClaimrLoading] = useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);
  const [getFirstToken, setGetFirstToken] = useState<boolean>(false);
  const [
    triggerGetClaimrTokenQuery,
    { data: claimrToken, isLoading: claimrIsLoading },
  ] = useLazyGetClaimrTokenQuery();
  const karmaSectionRef = useRef<HTMLDivElement>(null);
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { theme } = useTheme();

  const { data: refXp, isLoading: isRefXpLoading } = useGetUsersByRefXpQuery(
    undefined,
    { skip: !isAuth || !address }
  );

  const { data: questXp, isLoading: isQuestXpLoading } =
    useGetUsersByCompleteQuestXpQuery(undefined, { skip: !isAuth || !address });

  useEffect(() => {
    if (isAuth && address && !getFirstToken) {
      triggerGetClaimrTokenQuery({ wallet: address });
      setGetFirstToken(true);
    }
  }, [address, triggerGetClaimrTokenQuery, getFirstToken]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (claimrLoading) {
        setShowError(true);
      }
    }, CLAIMR_WIDGET_LOADING_TIMOUT);

    return () => clearTimeout(timer);
  }, [claimrLoading]);

  // If upstream redirects dropped query params, restore from localStorage
  useEffect(() => {
    try {
      const existing = new URLSearchParams(location.search);
      const keys = ["invite", "utm", "campaignId", "campaigId"] as const;
      let changed = false;
      keys.forEach((k) => {
        const v = localStorage.getItem(k);
        if (v && !existing.get(k)) {
          existing.set(k, v);
          changed = true;
        }
      });

      if (changed) {
        const qs = existing.toString();
        history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
      }
    } catch (e) {
      // no-op
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (claimrToken) {
      setClaimrLoading(false);
      setShowError(false);
    } else if (!claimrToken && isAuth && address && getFirstToken) {
      triggerGetClaimrTokenQuery({ wallet: address });
    }
  }, [claimrToken, address, triggerGetClaimrTokenQuery, getFirstToken]);

  const {
    data: banners,
    isLoading: isBannerLoading,
    error: _isBannerError,
  } = useGetPromotionBannersQuery();

  const isLoading =
    isBannerLoading || claimrIsLoading || isRefXpLoading || isQuestXpLoading;

  if (isLoading) {
    return <FullScreenLoader isLoading={isLoading} />;
  }

  return (
    <PageWrapper>
      <div
        className={`${styles.wrapper} ${theme === "dark" && styles.darkTheme}`}
      >
        {banners && <Banner banners={banners} />}
        {/*<WelcomeBanner data={welcomeBannerOnboarding} />*/}
        <div className={styles.connectBtn}>
          <HeaderConnect title="Connect Wallet" />
        </div>
        {!isAuth && (
          <div className={styles.cardWrapper}>
            {cardInfo.map((card) => (
              <div
                key={card.title}
                className={
                  card.title === "Community Rewards"
                    ? styles.centerCard
                    : styles.card
                }
              >
                <h3 className={styles.cardTitle}>{card.title}</h3>
                <img
                  className={styles.cardIcon}
                  src={card.icon}
                  alt={card.title}
                />
                <span className={styles.cardDescription}>
                  {card.description}
                </span>
              </div>
            ))}
          </div>
        )}

        <div ref={karmaSectionRef}>
          <h2
            className={
              isAuth
                ? styles.secondTitle
                : `${styles.secondTitle} ${styles.blur}`
            }
          >
            {onboardingTexts.karmaSectionTitle}
          </h2>
        </div>

        {isAuth && (
          <div className={styles.widgetWrapper}>
            {claimrLoading && !showError && (
              <div className={styles.loader}>
                <CircleLoader colorType="dark" isLoading={claimrLoading} />
              </div>
            )}
            {showError && (
              <h3 className={styles.errorTitle}>
                {onboardingTexts.errorTitle}
              </h3>
            )}
            {claimrToken && !showError && (
              <QuestClaimrWidget
                questId={questId}
                userToken={claimrToken}
                onLoaded={() => {
                  setClaimrLoading(false);
                }}
              />
            )}
            {/* <div className={styles.line}></div> */}
          </div>
        )}

        <h3
          className={
            isAuth ? styles.secondTitle : `${styles.secondTitle} ${styles.blur}`
          }
        >
          Individual Karma Leaderboard
        </h3>
        {isAuth ? (
          <Leaderboard
            isRef={false}
            isLoading={isQuestXpLoading}
            users={questXp || []}
          />
        ) : (
          <img src={LockedLiderBoard} alt="" />
        )}
        <h3
          className={
            isAuth ? styles.secondTitle : `${styles.secondTitle} ${styles.blur}`
          }
        >
          Referral Karma Leaderboard
        </h3>
        {isAuth ? (
          <Leaderboard isRef isLoading={isRefXpLoading} users={refXp || []} />
        ) : (
          <img src={LockedLiderBoard} alt="" />
        )}

        <div
          className={
            isAuth ? styles.infoWrapper : `${styles.infoWrapper} ${styles.blur}`
          }
        >
          <h3 className={styles.infoTitle}>{onboardingTexts.karmaInfoTitle}</h3>
          <span className={styles.infoDescription}>
            {onboardingTexts.karmaInfoDescription}
          </span>
          <ul className={styles.list}>
            {onboardingTexts.karmaList.map((item, index) => (
              <li key={index} className={styles.listItem}>
                <span>{item.title}</span>: {item.description}
              </li>
            ))}
          </ul>
          <span className={styles.secondDescription}>
            {onboardingTexts.karmaSecondDescription}
          </span>
        </div>
      </div>
    </PageWrapper>
  );
};

export default withQueryParams(["questId"], OnboardingPage);
